import haxios from './haxios';

// Thin wrappers over the staff-chat API (routes/chat.js on the live server).
// haxios attaches x-user-inits + bearer automatically and unwraps response.data,
// so every call here resolves to the JSON body (or rejects with an axios error).
//
// One permanent thread per entity:
//   entityType = 'customer'   → entityId = KeyCode  (e.g. "AB1234")
//   entityType = 'bdm_visit'  → entityId = visit_id (e.g. 50912)

const enc = encodeURIComponent;

export const fetchThread = (entityType, entityId) =>
  haxios.get(`/chat/${entityType}/${enc(entityId)}`);

export const postMessage = (entityType, entityId, { body, parentId = null, mentions } = {}) =>
  haxios.post(`/chat/${entityType}/${enc(entityId)}/messages`, {
    body,
    ...(parentId != null ? { parentId } : {}),
    ...(mentions && mentions.length ? { mentions } : {}),
  });

export const editMessage = (id, body) =>
  haxios.patch(`/chat/messages/${id}`, { body });

export const deleteMessage = (id) =>
  haxios.delete(`/chat/messages/${id}`);

// Toggle the caller's reaction for an emoji on a message. Returns the
// authoritative pill state: { messageId, emoji, action, count, reactedBySelf, users }.
export const toggleReaction = (id, emoji) =>
  haxios.post(`/chat/messages/${id}/reactions`, { emoji });

export const unreadCount = (entityType, entityId) =>
  haxios.get(`/chat/${entityType}/${enc(entityId)}/unread-count`);

export const markRead = (entityType, entityId) =>
  haxios.post(`/chat/${entityType}/${enc(entityId)}/read`, {});

// ---- read receipts ------------------------------------------------------
// Per-message "seen by" tracking. Opening a thread stamps a thread-level read
// marker server-side, but the per-message readBy[] list is only populated when
// clients report which messages the user actually saw. We report on scroll.
//
// Bulk (≤ 500 ids) — the workhorse; called as messages scroll into view.
//   POST /api/chat/read-receipts  { messageIds: [1,2,3] }
export const reportReads = (messageIds) =>
  haxios.post('/chat/read-receipts', { messageIds });

// Single message — mark one read (receipt). First read wins; read_at never moves.
export const markMessageRead = (id) =>
  haxios.post(`/chat/messages/${id}/read`, {});

// Full "seen by" roster for one message → { reads: [{ inits, name, at }, …] }.
// Used for an on-demand expanded receipt view (the thread payload already
// carries a summarised readBy[] for the inline affordance).
export const messageReads = (id) =>
  haxios.get(`/chat/messages/${id}/reads`);

// Bulk unread — one round-trip for a whole list of rows. Backend endpoint is
// being added (see docs/chat-bulk-unread-endpoint.md); callers tolerate a 404
// until it ships and simply render no badges.
//   POST /api/chat/unread-counts  { entities: [{ entityType, entityId }, ...] }
//   → { counts: { "customer:AB1234": 3, ... },   // unread per thread
//       totals: { "customer:AB1234": 12, ... } } // total comments (optional)
export const bulkUnread = (entities) =>
  haxios.post('/chat/unread-counts', { entities });

// Global "my unread messages" — every thread the caller is in, across ALL
// customers (not just ones on their scoped lists). The bell/dashboard alert
// can't enumerate those threads client-side, so the server does it.
//   GET /api/chat/my-unread
//   → { total, threadCount, threads: [{ entityType, entityId, label, unread,
//        lastAuthor, lastSnippet, lastAt, link }] }
// 404s on backends that haven't shipped it yet — callers fall back to deriving
// unread from the notifications feed (comment categories).
export const myUnread = () => haxios.get('/chat/my-unread');
